import React, { useState } from 'react';
import { ChipCondition } from '@/types/gql-op-types';
import { chipConditionBadgeLabel } from '@/lib/chipConditions';
import { Chip } from '../../lib/types';
import { formatDate, getAdopterDisplayName } from '../../lib/utils';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import DetailSection from '../../components/DetailSection/DetailSection';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import ChipHistoryTimeline from './ChipHistoryTimeline';
import { formatSecondaryContact } from './OwnerFormFields';
import { trackChipRemoveFromPetClicked } from '../../analytics';
import styles from './ChipDetailModal.module.scss';

interface ChipDetailModalProps {
  open: boolean;
  onClose: () => void;
  chip: Chip | null;
  loading?: boolean;
  /** True when this chip's pet has a completed adoption transfer. */
  petAdopted?: boolean;
  /** True when this chip's pet has a pending adoption transfer. Gates the
   * destructive Clear Owner / Remove from Pet actions, which the backend
   * rejects mid-transfer. */
  petPendingAdoption?: boolean;
  onTransfer: (chip: Chip) => void;
  onRecall: (chip: Chip) => void;
  onRemoveFromPet: (chip: Chip) => void;
  onChangeCondition: (chip: Chip) => void;
  /** Hidden when there's nothing the rescue is allowed to edit. */
  canEdit?: boolean;
  onEdit: (chip: Chip) => void;
}

export default function ChipDetailModal({
  open,
  onClose,
  chip,
  loading,
  petAdopted,
  petPendingAdoption,
  onTransfer,
  onRecall,
  onRemoveFromPet,
  onChangeCondition,
  canEdit,
  onEdit,
}: ChipDetailModalProps) {
  const [showHistory, setShowHistory] = useState(false);

  if (!chip) {
    return null;
  }

  function handleClose() {
    setShowHistory(false);
    onClose();
  }

  function handleTransfer() {
    handleClose();
    setTimeout(() => onTransfer(chip!), 200);
  }

  function handleRecall() {
    handleClose();
    setTimeout(() => onRecall(chip!), 200);
  }

  function handleRemoveFromPet() {
    trackChipRemoveFromPetClicked();
    handleClose();
    setTimeout(() => onRemoveFromPet(chip!), 200);
  }

  function handleEdit() {
    handleClose();
    setTimeout(() => onEdit(chip!), 200);
  }

  function handleChangeCondition() {
    handleClose();
    setTimeout(() => onChangeCondition(chip!), 200);
  }

  const hasOwner = !!chip.adopterFirstName;
  const hasPet = !!chip.petName;
  const isWorking = chip.condition === ChipCondition.WORKING;

  const fullAddress = chip.adopterStreet
    ? [
        chip.adopterStreet,
        chip.adopterApt ? `Apt ${chip.adopterApt}` : '',
        `${chip.adopterCity}, ${chip.adopterState} ${chip.adopterZip}`,
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  const secondaryContact = formatSecondaryContact({
    secondaryFirstName: chip.adopterSecondaryFirstName,
    secondaryLastName: chip.adopterSecondaryLastName,
    secondaryEmail: chip.adopterSecondaryEmail,
    secondaryPhone: chip.adopterSecondaryPhone,
  });

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      {showHistory ? (
        <ChipHistoryTimeline
          history={chip.history}
          chipLabel={chip.petName || chip.id}
          chipId={chip.id}
          onBack={() => setShowHistory(false)}
        />
      ) : (
        <>
          <ModalTitle>Chip details</ModalTitle>

          <DetailSection
            title="Pet Information"
            fields={[
              { label: 'Pet Name', value: chip.petName },
              { label: 'Species', value: chip.species },
              { label: 'Breed', value: chip.breed ? `${chip.breed}${chip.mixedBreed ? ' (mixed)' : ''}` : null },
              { label: 'Gender', value: chip.gender },
              { label: 'Date of Birth', value: formatDate(chip.dob) },
              { label: 'Spayed/Neutered', value: chip.spayedNeutered },
              { label: 'Color/Markings', value: chip.color },
              { label: 'Weight', value: chip.weight ? `${chip.weight} lbs` : null },
              ...(chip.additionalInfo ? [{ label: 'Additional Info', value: chip.additionalInfo }] : []),
            ]}
          />

          {hasOwner ? (
            <DetailSection
              title="Owner"
              fields={[
                {
                  label: 'Name',
                  value: [chip.adopterFirstName, chip.adopterLastName].filter(Boolean).join(' ') || null,
                },
                { label: 'Email', value: chip.adopterEmail },
                { label: 'Phone', value: chip.adopterPhone },
                { label: 'Address', value: fullAddress },
                ...(secondaryContact ? [{ label: 'Secondary Contact', value: secondaryContact }] : []),
                ...(chip.assignedTo === 'rescue' ? [{ label: 'Status', value: 'Rescue (in care)' }] : []),
              ]}
            />
          ) : (
            <DetailSection title="Owner" fields={[{ label: 'Status', value: 'No owner assigned' }]} />
          )}

          <DetailSection
            title="Chip"
            fields={[
              { label: 'Chip ID', value: chip.id },
              { label: 'Full ID', value: chip.fullChipId },
              {
                label: 'Status',
                value: (
                  <StatusBadge variant={chip.status === 'active' ? 'success' : 'neutral'}>
                    {chip.status === 'active' ? 'Active' : 'Stock'}
                  </StatusBadge>
                ),
              },
              {
                label: 'Condition',
                value: (
                  <span className={styles.conditionCell}>
                    <StatusBadge variant={isWorking ? 'success' : 'error'}>
                      {chipConditionBadgeLabel(chip.condition)}
                    </StatusBadge>
                    {/* Mutation is a one-way trip: non-WORKING chips 404 from
                        the reseller endpoint, so the action only appears here. */}
                    {isWorking && (
                      <Button variant="ghost" size="badge" onClick={handleChangeCondition}>
                        Mark Not Working
                      </Button>
                    )}
                  </span>
                ),
              },
              { label: 'Registered', value: formatDate(chip.registeredDate) },
              { label: 'Last Updated', value: formatDate(chip.lastUpdated) },
            ]}
          />

          <ModalActions>
            <Button variant="ghost" onClick={() => setShowHistory(true)}>
              History
            </Button>
            {canEdit && (
              <Button variant="secondary" onClick={handleEdit}>
                Edit
              </Button>
            )}
            {!hasOwner && !petAdopted && (
              <Button variant="green" onClick={handleTransfer}>
                Add Owner
              </Button>
            )}
            {hasOwner && !petAdopted && !petPendingAdoption && (
              <Button variant="danger" onClick={handleRecall}>
                Clear Owner
              </Button>
            )}
            {hasPet && !hasOwner && !petAdopted && !petPendingAdoption && (
              <Button variant="danger" onClick={handleRemoveFromPet}>
                Remove from Pet
              </Button>
            )}
          </ModalActions>
        </>
      )}
    </Modal>
  );
}
